import { useEffect, useRef, type ReactNode } from 'react'
import { initRail } from '../../lib/kitRail'

/**
 * The kit's carousel rail as a React component. The engine itself is `lib/kitRail.ts`, ported
 * near-verbatim from `app-platform/blocks/cupertino-kit.html` — see that file's header for why it
 * is a port rather than a rewrite, and for the rule that bugs are fixed in the kit first.
 *
 * React owns the slides; the engine owns scroll position, the clones and the dots. The two do not
 * fight because the slide list here is static: if it ever becomes dynamic, the engine has to be
 * torn down and re-initialised, which the effect's dependency on `slideKey` already does.
 *
 * Quil takes module 07 — the manual, captioned rail — and not 04. The kit's own reason for 07
 * existing is the one that matters in a writing app: a block someone reads at their own pace
 * should not move under them. So `transport` defaults to arrows, and autoplay is available but
 * unused.
 */
export interface RailProps {
  ariaLabel: string
  children: ReactNode
  /** Clone a set either side so the rail never reaches an end. */
  loop?: boolean
  /** Autoplay dwell in ms. Omit or 0 for a manual rail (Quil's only mode today). */
  autoplayMs?: number
  /** `arrows` is module 07's previous/next pair; `play` is 04's pause button. */
  transport?: 'arrows' | 'play' | 'none'
  /** Change this when the slide set changes, to force a clean re-init. */
  slideKey?: string
}

export default function Rail({
  ariaLabel,
  children,
  loop = false,
  autoplayMs = 0,
  transport = 'arrows',
  slideKey = '',
}: RailProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    return initRail(el, { loop, autoplayMs })
  }, [loop, autoplayMs, slideKey])

  return (
    <div className="carousel" ref={ref}>
      <div
        className="track"
        data-track
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      >
        {children}
      </div>
      <div className="hud">
        <div className="dots" role="tablist" aria-label={`Choose a card in ${ariaLabel}`} data-dots />
        {transport !== 'none' && (
          <div className="transport">
            {transport === 'arrows' ? (
              <>
                <button type="button" className="tbtn" data-prev aria-label="Previous">
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M10.3 1.7 4.6 7.4a.85.85 0 0 0 0 1.2l5.7 5.7 1.2-1.2L6.4 8l5.1-5.1Z" />
                  </svg>
                </button>
                <button type="button" className="tbtn" data-next aria-label="Next">
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M5.7 1.7 4.5 2.9 9.6 8l-5.1 5.1 1.2 1.2 5.7-5.7a.85.85 0 0 0 0-1.2Z" />
                  </svg>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="tbtn"
                data-playpause
                aria-pressed="false"
                aria-label="Pause the carousel"
              >
                <svg className="i-pause" viewBox="0 0 16 16" aria-hidden="true">
                  <rect x="3" y="2" width="3.6" height="12" />
                  <rect x="9.4" y="2" width="3.6" height="12" />
                </svg>
                <svg className="i-play" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M4 2.6v10.8a.8.8 0 0 0 1.22.68l8.6-5.4a.8.8 0 0 0 0-1.36l-8.6-5.4A.8.8 0 0 0 4 2.6Z" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
      <p className="sr-only" aria-live="polite" data-status />
    </div>
  )
}
